import React from "react";
import { Checkbox, Dropdown } from "semantic-ui-react";
import BootstrapTable from "react-bootstrap-table-next";
import { DropdownProps } from "semantic-ui-react/dist/commonjs/modules/Dropdown/Dropdown";
import { CheckboxProps } from "semantic-ui-react/dist/commonjs/modules/Checkbox/Checkbox";
import { getRawDateFromDayjs } from "../../../common/date_utils";
import {
  EntrataFloorPlans,
  EntrataNetworkMappings,
} from "../../../services/IntegrationApi";

import "./EntrataMappingContainer.css";
import { EntrataRowRemove } from "./EntrataRowRemove";
import { EntrataMappingRowSettingsCopy } from "./EntrataMappingRowSettingsCopy";
import { ModalProvider } from "../../../contexts/ModalContext";
import { DateInput } from "../../../components/DateInput";
import { getDateFromArray } from "../../../helpers/getDateFromArray";
import { NetworkInfo } from "./types";


// @ts-ignore
const getMappingKey = (row) => (row.l1Id ? `${row.l1Id}-${row.l2Id}-${row.l3Id}${row.l4Id ? `-${row.l4Id}` : ""}` : "");


interface EntrataMappingTableProps {
    entrataFloorPlans: EntrataFloorPlans[];
    networkMappings: NetworkInfo[];
    entrataSpaceConfigs: string[];
    nameOptions: Record<string, string>;
    l2StatusById: Record<number, string | null>;
    onNetworkInfoChange: (networkInfo: NetworkInfo) => void;
    setNetworkMapping: React.Dispatch<React.SetStateAction<EntrataNetworkMappings[]>>;
    userId?: number;
    propertyId?: number;
  }

 export const EntrataMappingTable: React.FunctionComponent<EntrataMappingTableProps> = ({
    entrataFloorPlans,
    networkMappings,
    entrataSpaceConfigs,
    nameOptions,
    l2StatusById,
    onNetworkInfoChange,
    setNetworkMapping,
    userId,
    propertyId,
  }) => {
    if (!entrataFloorPlans?.length) {
      return <div />;
    }
  
    const rowClasses = (row: NetworkInfo, rowIndex: number): string => {
      let classes = "mapping-row";
  
      if (row.updated) {
        classes = "updated-mapping-row mapping-row";
      }
  
      if (row.valid != undefined) {
        if (!row.valid) {
          classes = "invalid-mapping-row mapping-row";
        }
      }
  
      return classes;
    };
  
    const columns = [
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "name",
        text: "L2-L4",
        formatter: (cell: string, row: NetworkInfo) => {
          const value = getMappingKey(row);
  
          if (row.extra) {
            const options = Object.entries(nameOptions)
              .filter(([key]) => {
                const l2Id = Number(key.split("-")[1]);
                const status = l2StatusById[l2Id];
                return status !== "ARCHIVED" && status !== "CLOSED";
              })
              .map(([value, text]) => ({
                value,
                text,
              }));
  
            return (
              <Dropdown
                value={value}
                options={options}
                placeholder="Select name"
                onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                  if (typeof data.value === "string") {
                    const ids = data.value.split("-");
                    const network = {
                      ...row,
                      l1Id: Number(ids[0]),
                      l2Id: Number(ids[1]),
                      l3Id: Number(ids[2]),
                    };
  
                    if (ids[3]) {
                      network.l4Id = Number(ids[3]);
                    }
  
                    onNetworkInfoChange(network);
                  }
                }}
              />
            );
          }
  
          // @ts-ignore
          return nameOptions[value];
        },
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${getMappingKey(row)}-${rowIndex}-${colIndex}`,
        }),
        dataField: "entrataFloorPlan",
        text: "Entrata Floor Plans",
        formatter: (cell: string, row: NetworkInfo) => {
          const options = entrataFloorPlans.map((floorPlan) => ({
            value: floorPlan.name,
            text: floorPlan.name,
          }));
  
          return (
            <Dropdown
              value={cell}
              options={options}
              placeholder="Select floor plan"
              onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                if (typeof data.value === "string") {
                  onNetworkInfoChange({
                    ...row,
                    entrataFloorPlan: data.value,
                  });
                }
              }}
            />
          );
        },
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${getMappingKey(row)}-${rowIndex}-${colIndex}`,
        }),
        dataField: "entrataUnitSpace",
        text: "Entrata Unit Space",
        formatter: (cell: string, row: NetworkInfo) => {
          let options = [{
            value: "All",
            text: "All"
          }];
  
          if (row.entrataFloorPlan) {
            const unitTypes = entrataFloorPlans.filter((floorPlan) => floorPlan.name === row.entrataFloorPlan);
  
            if (unitTypes.length === 1) {
              options = unitTypes[0].unitTypes.map((unitType) => ({
                value: unitType.name,
                text: unitType.name,
              }));
  
              options.push({value: "All", text: "All"});
            }
          }
  
          return (
            <Dropdown
              value={cell}
              options={options}
              placeholder="Select unit type"
              onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                if (typeof data.value === "string") {
                  onNetworkInfoChange({
                    ...row,
                    entrataUnitSpace: data.value,
                  });
                }
              }}
            />
          );
        },
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "entrataSpaceConfiguration",
        text: "Entrata Space Configuration",
        formatter: (cell: string, row: NetworkInfo) => {
          const options = Array.from(new Set(entrataSpaceConfigs)).map((x) => ({
            value: x,
            text: x,
          }));
  
          return (
            <Dropdown
              clearable
              value={cell}
              options={options}
              placeholder="Select space config"
              onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
                if (typeof data.value === "string") {
                  onNetworkInfoChange({
                    ...row,
                    entrataSpaceConfiguration: data.value,
                  });
                }
              }}
            />
          );
        },
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "dateType",
        text: "Lease Start or End Date",
        formatter: (cell: string, row: NetworkInfo) => (
          <Dropdown
            value={cell}
            placeholder="Select date range"
            onChange={(event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
              if (typeof data.value === "string") {
                onNetworkInfoChange({
                  ...row,
                  dateType: data.value,
                });
              }
            }}
            options={[
              {
                value: "START_DATE",
                text: "Start Date",
              },
              {
                value: "END_DATE",
                text: "End Date",
              },
            ]}
          />
        ),
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "entrataStartDate",
        text: "Date Range Start",
        formatter: (value: number[], row: NetworkInfo) => {
          const dateValue = value ? getDateFromArray(value) : null;
          const maxDate = row.entrataEndDate ? getDateFromArray(row.entrataEndDate) : undefined;
          const onChange = (selectedDate: any) => {
            if (!selectedDate) return;
  
            onNetworkInfoChange({
              ...row,
              entrataStartDate: getRawDateFromDayjs(selectedDate),
            });
          };
  
          return <DateInput value={dateValue} maxDate={maxDate} onChange={onChange} />;
        },
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "entrataEndDate",
        text: "Date Range End",
        formatter: (value: number[], row: NetworkInfo) => {
          const minDate = row.entrataStartDate ? getDateFromArray(row.entrataStartDate) : undefined;
  
          const onChange = (selectedDate: any) => {
            if (!selectedDate) return;
  
            onNetworkInfoChange({
              ...row,
              entrataEndDate: getRawDateFromDayjs(selectedDate),
            });
          };
          const dateValue = value ? getDateFromArray(value) : null;
  
          return <DateInput value={dateValue || minDate} onChange={onChange} minDate={minDate} />;
        },
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "allowAccess",
        text: "Enabled",
        formatter: (value: boolean, row: NetworkInfo) => (
          <Checkbox
            toggle
            checked={value}
            onChange={(event: React.FormEvent<HTMLInputElement>, data: CheckboxProps) =>
              onNetworkInfoChange({
                ...row,
                allowAccess: !!data.checked,
              })
            }
          />
        ),
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "id",
        text: "",
        formatter: (value: number, row: NetworkInfo) => (
          <ModalProvider>
            <EntrataMappingRowSettingsCopy
              row={row}
              nameOptions={nameOptions}
              networkMappings={networkMappings}
              setNetworkMapping={setNetworkMapping}
            />
          </ModalProvider>
        ),
      },
      {
        attrs: (cell: string, row: NetworkInfo, rowIndex: number, colIndex: number) => ({
          key: `${row.rowId}-${rowIndex}-${colIndex}`,
        }),
        dataField: "remove",
        text: "",
        formatter: (value: number, row: NetworkInfo) => (
          <ModalProvider>
            <EntrataRowRemove
              networkMappings={networkMappings}
              row={row}
              setNetworkMapping={setNetworkMapping}
              userId={userId}
              propertyId={propertyId}
            />
          </ModalProvider>
        ),
      },
    ];
  
    return (
      <BootstrapTable
        columns={columns}
        rowClasses={rowClasses}
        data={networkMappings}
        keyField="rowId"
        headerClasses="entrata-header"
      />
    );
  };
  